import CoinPrompt from '@scenes/interface/prompts/CoinPrompt'
import ErrorPrompt from './ErrorPrompt'
import ItemPrompt from '@scenes/interface/prompts/ItemPrompt'
import InputPrompt from '@scenes/interface/prompts/InputPrompt'
import LoadingPrompt from '@scenes/interface/prompts/LoadingPrompt'
import WindowPrompt from '@scenes/interface/prompts/WindowPrompt'
import StampPrompt from '@scenes/interface/prompts/StampPrompt';
import RewardPackPrompt from '@scenes/interface/prompts/RewardPackPrompt';
import NPCDialog from '@scenes/interface/prompts/NPCDialog'

import MailErrorPrompt from '@scenes/interface/prompts/MailErrorPrompt'
import MailSuccessPrompt from '@scenes/interface/prompts/MailSuccessPrompt'


export default class PromptController {

    constructor(scene) {
        this.interface = scene

        this.crumbs = this.interface.crumbs
        this.network = this.interface.network
        this.world = this.interface.world

        this.prompts = []
        this.overlayZIndexHidden = false

        this.coin = this.createPrompt(CoinPrompt)
        this.error = this.createPrompt(ErrorPrompt)
        this.item = this.createPrompt(ItemPrompt)
        this.input = this.createPrompt(InputPrompt)
        this.loading = this.createPrompt(LoadingPrompt)
        this.window = this.createPrompt(WindowPrompt)

        this.mailError = this.createPrompt(MailErrorPrompt)
        this.mailSuccess = this.createPrompt(MailSuccessPrompt)
        this.stamp = this.createPrompt(StampPrompt)
        this.rewardPack = this.createPrompt(RewardPackPrompt)
        this.npcDialog = this.createPrompt(NPCDialog)
    }

    get coins() {
        return this.world.client.coins
    }

    get isPromptVisible() {
        return this.prompts.some(prompt => prompt.visible === true)
    }

    createPrompt(promptClass) {
        const prompt = new promptClass(this.interface, 760, 480)

        this.prompts.push(prompt)
        this.interface.add.existing(prompt)

        return prompt
    }

    showCoin(
        coins = 0,
        totalStamps = 0,
        collectedStamps = 0,
        room = undefined,
        relevantStamps = [],
        doubleCoins = false,
    ) {
        // Play coins collection sound (CPI: SFX/UI/Coin/CoinsLoop)
        this.playSfx('ui-coins', 0.5)
        
        this.coin.show(
            coins,
            totalStamps,
            collectedStamps,
            room,
            relevantStamps,
            doubleCoins
        );
    }

    showError(text, buttonText = 'Okay', callback = () => this.error.close()) {
        // Play error feedback sound
        this.playSfx('ui-error', 0.4)
        
        this.error.show(text, buttonText, callback)
    }

    showItem(item) {
        this.item.showItem(item)
    }

    showFurniture(furniture) {
        this.item.showFurniture(furniture)
    }

    showIgloo(igloo) {
        if (this.world.client.igloos.includes(igloo)) {
            return this.showError('You already have this igloo.')
        }

        let text = `Would you like to buy ${this.crumbs.igloos[igloo].name} for ${this.crumbs.igloos[igloo].cost} coins. You currently have ${this.world.client.coins} coins.`

        this.showWindow(text, 'dual', () => {
            this.network.send('add_igloo', { igloo: igloo })
            this.interface.prompt.window.close()
        })
    }

    showFloor(floor) {
        if (floor == this.world.room.args.flooring) {
            return this.showError('You already have this flooring.')
        }

        let text = `Would you like to buy ${this.crumbs.flooring[floor].name} for ${this.crumbs.flooring[floor].cost} coins. You currently have ${this.world.client.coins} coins.`

        this.showWindow(text, 'dual', () => {
            this.network.send('update_flooring', { flooring: floor })
            this.interface.prompt.window.close()
        })
    }

    showWindow(text, buttonLayout = 'single', callback = () => this.window.close(), noCallback = () => this.window.close()) {
        this.window.show(text, buttonLayout, callback, noCallback)
    }

    showLoading(text) {
        this.loading.show(text)
    }

    showMailError(text) {
        this.mailError.show(text)
    }

    showMailSuccess(text) {
        this.mailSuccess.show(text)
    }

    showAdopt(typeId) {
        const maxPets = 18

        if (this.world.client.pets.length >= maxPets) {
            return this.showError(this.getFormatString('max_pets', maxPets))
        }

        this.item.showAdopt(typeId)
    }

    showAdoptName(typeId) {
        this.input.showAdoptName(typeId)
    }

    showPetFood(pet) {
        if (this.coins < 10) return this.showLowCoins()

        this.item.showPetFood(pet)
    }

    showPetBath(pet) {
        if (this.coins < 5) return this.showLowCoins()

        this.item.showPetBath(pet)
    }

    showPetGum(pet) {
        if (this.coins < 5) return this.showLowCoins()

        this.item.showPetGum(pet)
    }

    showPetCookie(pet) {
        if (this.coins < 5) return this.showLowCoins()

        this.item.showPetCookie(pet)
    }

    showLowCoins() {
        this.showError(this.getString('low_coin_warn'))
    }

    showSecret(item) {
        this.interface.loadWidget('CatalogSecret', false, { item: item, type: 'clothing' })
    }

    showFurnitureSecret(item) {
        this.interface.loadWidget('CatalogSecret', false, { item: item, type: 'furniture' })
    }

    showStamp(text, category, rank) {
        //todo: queue stamps
        // Play celebration sound for stamp unlocks
        this.playSfx('ui-celebration', 0.5)
        
        this.stamp.show(text, category, rank);
    }

    /**
     * Show reward pack prompt for CPI-style login rewards
     * @param {Array} packs - Array of eligible reward packs from WebAPI
     * @param {Function} onComplete - Callback when all packs have been claimed
     */
    showRewardPacks(packs, onComplete = null) {
        // Play celebration sound for reward packs
        this.playSfx('ui-celebration', 0.5)
        
        this.rewardPack.show(packs, onComplete);
    }

    /**
     * Show NPC dialog prompt
     * @param {Object} data - NPC dialog data from server
     */
    showNPCDialog(data) {
        this.npcDialog.show(data)
    }

    /**
     * Update NPC dialog with new data
     * @param {Object} data - Updated dialog data
     */
    updateNPCDialog(data) {
        if (this.npcDialog.visible) {
            this.npcDialog.updateDialog(data)
        }
    }

    /**
     * Close NPC dialog
     */
    closeNPCDialog() {
        this.npcDialog.close()
    }

    getString(...args) {
        return this.interface.getString(...args)
    }

    getFormatString(id, ...args) {
        return this.interface.getFormatString(id, ...args)
    }

    hideAll() {
        this.loading.close()
    }

    /**
     * Hide z-index on all active HTML overlay widgets so prompts can appear above them
     */
    hideOverlayZIndex() {
        if (this.overlayZIndexHidden) {
            return
        }
        this.overlayZIndexHidden = true

        const widgets = this.interface.widgets?.activeWidgets || {}
        for (const key in widgets) {
            const widget = widgets[key]
            if (typeof widget?.hideOverlayZIndex === 'function') {
                widget.hideOverlayZIndex()
            }
        }
    }

    /**
     * Restore z-index on all active HTML overlay widgets after prompts close
     */
    showOverlayZIndex() {
        if (!this.overlayZIndexHidden) {
            return
        }
        this.overlayZIndexHidden = false

        const widgets = this.interface.widgets?.activeWidgets || {}
        for (const key in widgets) {
            const widget = widgets[key]
            if (typeof widget?.showOverlayZIndex === 'function') {
                widget.showOverlayZIndex()
            }
        }
    }
    
    /**
     * Play a UI sound effect (CPI-style)
     * @param {string} key - Sound key
     * @param {number} volume - Volume (0-1)
     */
    playSfx(key, volume = 0.35) {
        try {
            const soundManager = this.interface?.soundManager
            if (soundManager && this.interface?.cache?.audio?.exists(key)) {
                soundManager.playSfx(key, { volume, allowOverlap: true })
            }
        } catch (e) {
            // Silently fail if sound system not available
        }
    }

}
